import { baseApiUrl, fetcher, handleError, ServerErrorResponse } from 'data_sources/constants';
import {
  AttachmentResponse,
  CreateAttachmentParams,
  UpdateAttachmentParams,
} from 'views/App/Portal/Policy/_types/manuals/draft';

export default function useManageAttachment() {
  const createAttachment = async (
    params: CreateAttachmentParams,
    onSuccess?: (finalUrl: string, attachmentId: string) => void,
    onError?: (err: ServerErrorResponse) => void
  ) => {
    const {
      manualId,
      file,
      name,
      // userId,
      size,
      globalAttachmentId,
      jurisdictionAttachmentId,
    } = params;

    try {
      const createResp = await fetcher({
        method: 'POST',
        url: `${baseApiUrl()}/lexiverse-manual-api/v1/manuals/${manualId}/attachments`,
        body: {
          name,
          size,
          // userId,
          globalAttachmentId,
          jurisdictionAttachmentId,
        },
      });

      const attachmentId = createResp.id as string;
      const finalUrl = createResp.url as string;

      if (onSuccess) {
        onSuccess(finalUrl, attachmentId);
      }
    } catch (err) {
      if (onError) {
        handleError(err as Error, { onError });
      } else {
        console.error('createAttachment error:', err);
      }
    }
  };

  const getAttachment = async ({
    manualId,
    attachmentId,
  }: {
    manualId: string;
    attachmentId: string;
  }): Promise<AttachmentResponse> => {
    const resp = await fetcher({
      method: 'GET',
      url: `${baseApiUrl()}/lexiverse-manual-api/v1/manuals/${manualId}/attachments/${attachmentId}`,
    });
    return resp as AttachmentResponse;
  };

  const updateAttachment = async (
    params: UpdateAttachmentParams,
    onSuccess?: (updatedAttachment: AttachmentResponse) => void,
    onError?: (err: ServerErrorResponse) => void
  ) => {
    const { manualId, attachmentId, name, size } = params;

    try {
      const resp = await fetcher({
        method: 'PUT',
        url: `${baseApiUrl()}/lexiverse-manual-api/v1/manuals/${manualId}/attachments/${attachmentId}`,
        body: {
          name,
          size,
        },
      });
      if (onSuccess) {
        onSuccess(resp as AttachmentResponse);
      }
    } catch (err) {
      if (onError) {
        handleError(err as Error, { onError });
      } else {
        console.error('updateAttachment error:', err);
      }
    }
  };

  function base64ToBlob(base64: string, mime: string): Blob {
    base64 = base64.replace(/\s/g, '');
    const binary = atob(base64);
    const array = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      array[i] = binary.charCodeAt(i);
    }
    return new Blob([array], { type: mime });
  }

  // Uploads a file using a presigned URL via a PUT request.
  async function uploadFile(presignedUrl: string, file: File): Promise<void> {
    const response = await fetch(presignedUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
      },
    });
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`S3 Upload Failed | Status: ${response.status} | Error: ${errorText}`);
      throw new Error(`S3 upload failed: ${response.statusText} | Details: ${errorText}`);
    }
  }

  const deleteAttachment = async (
    manualId: string,
    attachmentId: string,
    onSuccess?: () => void,
    onError?: (err: ServerErrorResponse) => void
  ) => {
    try {
      await fetcher({
        method: 'DELETE',
        url: `${baseApiUrl()}/lexiverse-manual-api/v1/manuals/${manualId}/attachments/${attachmentId}`,
      });
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      if (onError) {
        handleError(err as Error, { onError });
      } else {
        console.error('deleteAttachment error:', err);
      }
    }
  };

  return {
    createAttachment,
    getAttachment,
    updateAttachment,
    base64ToBlob,
    uploadFile,
    deleteAttachment,
  };
}
